import React, { useEffect, useRef, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  Box,
  Button,
  Heading,
  HStack,
  useToast,
  Text,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getDraws,
  getPendingDraws,
  getCurrentDraws,
  getDrawHistory,
  startScript,
  stopScript,
  getScriptStatus,
  shiftToHistory,
} from '../actions/drawActions';

import { getGameSettings, uploadGame } from '../actions/settingsAction';
import GenericTable from '../components/GenericTable';
import ScriptStatusBox from './ScriptStatusBox';
import CountdownTimer from '../components/CountdownTimer';

const DrawManagement = () => {
  const dispatch = useDispatch();
  const { slug } = useParams();
  const toast = useToast();
  const [copiedId, setCopiedId] = useState(null);
  const { draws, drawHistory, currentDraws, pendingDraws, scriptStatus, loading, error } =
    useSelector((state) => state.draw);
  const { settings, sloading, serror } = useSelector((state) => state.setting);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // NEW: keep track of rows hidden due to expiry, and a version to force re-render after Set changes
  const [hiddenCurrentIds, setHiddenCurrentIds] = useState(() => new Set());
  const [version, setVersion] = useState(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const gstatus = await dispatch(getGameSettings());
        await dispatch(getDraws());
        await dispatch(getPendingDraws());
        await dispatch(getCurrentDraws());
        const status = await dispatch(getScriptStatus());
        if (status) {
          toast({
            title: 'Script status fetched',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
        if (gstatus) {
          toast({
            title: 'Game Settings fetched',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
        await dispatch(getDrawHistory({ page: currentPage, limit: pageSize }));
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchData();
  }, [dispatch, currentPage, pageSize]);

  const handleStartScript = async () => {
    try {
      await dispatch(startScript());
      toast({
        title: 'Script started',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error starting script',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStopScript = async () => {
    try {
      await dispatch(stopScript());
      toast({
        title: 'Script stopped',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error stopping script',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const uploadGameScript = async () => {
    try {
      if (settings?.[0]?.lastGameUpload === 0) {
        toast({
          title: 'Game already upload',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        return;
      } else {
        let status = await dispatch(uploadGame());
        if (status) {
          toast({
            title: status.msg,
            status: 'success - ' + status.statusCode,
            duration: 3000,
            isClosable: true,
          });
          return;
        }
      }
    } catch (error) {
      toast({
        title: 'Error uploading script',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleShiftToHistory = async () => {
    try {
      if (settings?.[0]?.lastBackupDiffInDays === 0) {
        toast({
          title: 'No, Draws shifted to history',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        return;
      } else {
        const result = await dispatch(shiftToHistory());
        await dispatch(getDraws());
        await dispatch(getDrawHistory({ page: 1, limit: pageSize }));
        await dispatch(getGameSettings());
        console.log('s2h   ', result);
        toast({
          title: 'Draws shifted to history',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error shifting draws',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCopyDrawId = async (drawId) => {
    try {
      await navigator.clipboard.writeText(drawId);
      setCopiedId(drawId);
      toast({
        title: 'Draw ID copied',
        status: 'success',
        duration: 1000,
        isClosable: true,
      });
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      toast({
        title: 'Failed to copy Draw ID',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // NEW: on countdown expiry -> hide row immediately and fetch fresh current draws (no page reload)
  const handleDrawExpired = async (drawId) => {
    // Optimistically hide the expired row
    setHiddenCurrentIds((prev) => {
      const next = new Set(prev);
      next.add(drawId);
      return next;
    });
    setVersion((v) => v + 1);

    // Debounce multiple expiries
    if (refreshingRef.current) return;
    refreshingRef.current = true;

    try {
      await dispatch(getCurrentDraws());
    } catch (error) {
      toast({
        title: 'Error refreshing draws',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      // Allow newly-fetched list to render; clear hidden ids
      setHiddenCurrentIds(new Set());
      setVersion((v) => v + 1);
      refreshingRef.current = false;
    }
  };

  // NEW: Timer wrapper that doesn't render the "Expired" label
  const AutoRefreshTimer = ({ targetTime, onExpire }) => {
    const [active, setActive] = useState(true);
    return active ? (
      <CountdownTimer
        targetTime={targetTime}
        onExpire={() => {
          setActive(false); // hide timer entirely after expiry
          onExpire?.();
        }}
      />
    ) : null;
  };

  // Optional: when Redux replaces currentDraws (e.g., by other actions),
  // reset hidden set so latest data shows unfiltered
  useEffect(() => {
    setHiddenCurrentIds(new Set());
    setVersion((v) => v + 1);
  }, [currentDraws]);

  const columns = [
    {
      Header: 'Draw ID',
      accessor: 'draw_id',
      Cell: ({ value, row }) => {
        const gameNumber =
          row?.original?.gameNumber ??
          row?.original?.game_number ??
          row?.original?.gameCode ??
          row?.original?.game_code ??
          row?.original?.game_id ??
          '';
        const drawEpoch = Number(row?.original?.draw_time_epoc);
        const timeLeftSeconds =
          Number.isFinite(drawEpoch) ? Math.max(0, Math.floor((drawEpoch - Date.now()) / 1000)) : null;
        const query = new URLSearchParams({
          draw_id: value,
          game_id: gameNumber,
          next_draw: 'false',
          manual: 'false',
        });
        if (timeLeftSeconds !== null) {
          query.set('time_left', String(timeLeftSeconds));
        }
        const basePath = slug ? `/${slug}/draws` : '/draws';
        const href = `${basePath}?${query.toString()}`;

        return (
          <div className="flex items-center gap-2">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
              onClick={(e) => e.stopPropagation()}
              title="Open draw details in a new tab"
            >
              {value}
            </a>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyDrawId(value);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label="Copy Draw ID"
              title="Copy Draw ID"
            >
              {copiedId === value ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4 text-gray-500" />
              )}
            </button>
          </div>
        );
      },
    },
    { Header: 'Game', accessor: 'game_name' },
    { Header: 'Draw Time', accessor: 'draw_time', Cell: ({ value }) => new Date(value).toLocaleString() },
    { Header: 'Result', accessor: 'result', Cell: ({ value }) => value || '-' },
    { Header: 'Bonus', accessor: 'bonus', Cell: ({ value }) => value || '-' },
  ];

  const currentdraw_columns = [
    ...columns,
    {
      Header: 'Time Left',
      accessor: 'draw_time_epoc',
      Cell: ({ value, row }) => (
        <AutoRefreshTimer targetTime={value} onExpire={() => handleDrawExpired(row.original.draw_id)} />
      ),
    },
  ];

  const historyColumns = [
    ...columns,
    {
      Header: 'Shifted At',
      accessor: 'shifted_at',
      Cell: ({ value, row }) => (
        <CountdownTimer
          targetTime={value}
          onExpire={() => {
            console.log(`Draw expired: ID ${row.original.id}`);
            refreshCurrentDraws();
          }}
        />
      ),
    },
  ];

  // Kept from your original code (slightly adjusted: no direct mutation of scriptStatus)
  const refreshCurrentDraws = async () => {
    try {
      console.log('refresh current draw');
      await dispatch(getCurrentDraws());
      toast({
        title: 'Reload current draw',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error getting current draw',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading && sloading) return <Box>Loading...</Box>;
  if (error) return <Box>Error: {error}</Box>;
  console.log('setting json ', settings?.[0]);

  // NEW: filter out expired rows immediately (no “Expired” text; row disappears)
  const currentDrawsFiltered = (currentDraws || []).filter((d) => !hiddenCurrentIds.has(d.draw_id));

  return (
    <Box p={5} key={version}>
      <VStack spacing={5} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>
            Draw Management
          </Heading>
          <HStack spacing={4} mb={4}>
            <div>
              <pre>lastGameUpload: {settings?.[0]?.lastGameUpload ?? 'Not Available'}</pre>
            </div>
            <Button
              colorScheme={settings?.[0]?.lastGameUpload === 0 ? 'red' : 'green'}
              onClick={uploadGameScript}
            >
              Upload Game
            </Button>
            <Button disabled={false}>Start Script</Button>

            <Button
              colorScheme={scriptStatus?.isRunning ? 'red' : 'green'}
              onClick={scriptStatus?.isRunning ? handleStopScript : handleStartScript}
            >
              {scriptStatus?.isRunning ? 'Stop Script' : 'Start Script'}
            </Button>
            <Button
              colorScheme={settings?.[0]?.lastBackupDiffInDays === 0 ? 'red' : 'blue'}
              onClick={handleShiftToHistory}
            >
              Shift to History
            </Button>
            <ScriptStatusBox
              scriptStatus={scriptStatus}
              settingStatus={settings?.[0] ?? {}}
              loading={sloading}
            />
            ;
          </HStack>
        </Box>

        <Tabs>
          <TabList>
            <Tab>Current Draw</Tab>
            <Tab>Pending Draw</Tab>
            <Tab>All Draws</Tab>
            <Tab>Draw History</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <GenericTable
                isDrawTable={true}
                columns={currentdraw_columns}
                data={currentDrawsFiltered}
                initialPageSize={10}
              />
            </TabPanel>
            <TabPanel>
              <GenericTable isDrawTable={true} columns={columns} data={pendingDraws || []} initialPageSize={50} />
            </TabPanel>
            <TabPanel>
              <GenericTable isDrawTable={true} columns={columns} data={draws || []} initialPageSize={50} />
            </TabPanel>
            <TabPanel>
              <GenericTable columns={historyColumns} data={drawHistory?.draws || []} initialPageSize={10} />
              {drawHistory?.pagination && (
                <Flex justify="flex-end" mt={4}>
                  <Button
                    isDisabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    size="sm"
                    mx={2}
                  >
                    Previous
                  </Button>
                  <Text mx={4} alignSelf="center">
                    Page {currentPage} of {drawHistory.pagination.pages}
                  </Text>
                  <Button
                    isDisabled={currentPage === drawHistory.pagination.pages}
                    onClick={() => setCurrentPage((prev) => Math.min(drawHistory.pagination.pages, prev + 1))}
                    size="sm"
                    mx={2}
                  >
                    Next
                  </Button>
                </Flex>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default DrawManagement;
